import React, { useEffect, useState } from 'react';
import { Localizer } from 'services/Localizer';
import Spinner from 'shared/components/Spinner';
import InlineAlert from 'shared/edit/InlineAlert';
import { DropdownField, TextBoxField, ValidationMessages } from 'shared/edit/fields';
import ReCaptchaDisclaimer from 'shared/edit/fields/ReCaptchaDisclaimer';
import { useAxiosFormSubmitter } from 'shared/hooks/useBaseAxiosFormSubmitter';
import RocForm from 'shared/edit/RocForm';
import SimpleCheckbox from 'shared/edit/fields/SimpleCheckbox';
import Axios, { AxiosError } from 'axios';
import AxiosHelper from 'services/AxiosHelper';
import { error } from 'util';
import LazyImage from 'shared/assets/LazyImage';
import { DropdownOption } from '../../../../../Roc.SharedClientApp/types/fields';
import { Field } from 'react-final-form';
import { State } from '../../../../../Roc.SharedClientApp/types/address';
import Wysiwyg from 'features/hcl/Wysiwyg';
import classnames from 'classnames';

interface ProductCatalogsValues {
	fullName: string;
	email: string;
	phone: string;
	addressType: AddressType;
	facilityName: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
	isEmailList: boolean;
	catalogs: string[];
}

export interface UseStatesResults {
	states: State[] | null;
}

export interface UseCatalogsResults {
	productCatalogs: ProductCatalog[] | null;
}

export interface ProductCatalog {
	name: string;
	text: string;
	url: string;
	imageUrl: string;
	digitalOnly: boolean;
}

export enum AddressType {
	Business = 'Business',
	Residential = 'Residential',
}

export const AddressTypeOption = Object.keys(AddressType).map(
	(key) => ({ value: key, text: AddressType[key] } as DropdownOption),
);

/**
 * Product Catalogs component
 */
function ProductCatalogs() {
	const [isEmailList, setIsEmailList] = useState(false);
	const [catalogNames] = useState<string[]>([]);
	const [initialValues] = useState<ProductCatalogsValues>({
		fullName: '',
		email: '',
		phone: '',
		addressType: AddressType.Business,
		facilityName: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		postalCode: '',
		isEmailList: false,
		catalogs: [],
	});

	const productCatalogsFormSubmitter = useAxiosFormSubmitter<ProductCatalogsValues>(
		{
			url: '/ajax/product-catalogs',
		},
		'ProductCatalogs',
	);

	const [states, setStates] = useState<State[] | null>(null);
	const [allCatalogs, setAllCatalogs] = useState<ProductCatalog[] | null>(null);

	useEffect(() => {
		try {
			if (!states) {
				(async () => {
					const response = await AxiosHelper.get<UseStatesResults>('/ajax/product-catalogs/states');
					setStates(response.data.states);
				})();
			}
		} catch (err) {
			if (Axios.isCancel(err)) {
				return;
			}
			const axiosError = err as AxiosError;
			error(`An error occurred while loading states: ${axiosError.message}`);
		}

		try {
			if (!allCatalogs) {
				(async () => {
					const response = await AxiosHelper.get<UseCatalogsResults>('/ajax/product-catalogs/catalogs');
					setAllCatalogs(response.data.productCatalogs);
				})();
			}
		} catch (err) {
			if (Axios.isCancel(err)) {
				return;
			}
			const axiosError = err as AxiosError;
			error(`An error occurred while loading catalogs: ${axiosError.message}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function updateCatalogNames(catalogName: string) {
		const match = catalogNames.find((e) => e === catalogName);

		if (match) {
			const index = catalogNames.indexOf(catalogName, 0);

			catalogNames.splice(index, 1);
		} else {
			catalogNames.push(catalogName);
		}
	}

	return (
		<div className="roc-container">
			{!productCatalogsFormSubmitter.submitted ? (
				<RocForm<ProductCatalogsValues>
					onSubmit={(formValues) => {
						const submitValues = { ...formValues };

						submitValues.isEmailList = isEmailList;
						submitValues.catalogs = catalogNames;
						return productCatalogsFormSubmitter.handleSubmit(submitValues);
					}}
					initialValues={initialValues}
					render={({ handleSubmit, submitError, submitting }) => {
						return (
							<form onSubmit={handleSubmit}>
								<h1 className="roc-page-title roc-page-title__content-page">
									{Localizer('ProductCatalogsTitle')}
								</h1>

								<div className="roc-catalog-wrapper">
									<div>
										<Field name="catalogs">
											{({ input, meta }) => (
												<>
													{allCatalogs ? (
														<ul className="roc-brands roc-catalog">
															{allCatalogs.map((catalog, index) => (
																<li className="roc-brands__item" key={index}>
																	<div className="roc-brands__card">
																		<a
																			href={catalog.url}
																			className="roc-brands__link"
																			target="_blank"
																			rel="noopener noreferrer"
																		>
																			<div className="roc-brands__image-wrapper">
																				<LazyImage
																					src={catalog.imageUrl}
																					alt={catalog.name}
																				/>
																			</div>
																		</a>

																		<div
																			className={classnames(
																				'roc-brands__name-wrapper',
																				{
																					'roc-brands__name-wrapper--digitalOnly':
																						catalog.digitalOnly,
																				},
																			)}
																		>
																			{!catalog.digitalOnly ? (
																				<input
																					className="roc-form-input__input"
																					id={'catalog-' + index}
																					type="checkbox"
																					onChange={() =>
																						updateCatalogNames(catalog.name)
																					}
																				/>
																			) : null}
																			<h2 className="roc-brands__name">
																				{catalog.name}
																			</h2>
																			{catalog.digitalOnly ? (
																				<a
																					href={catalog.url}
																					className="roc-brands__link"
																					target="_blank"
																					rel="noopener noreferrer"
																				>
																					{Localizer(
																						'ProductCatalogsDigitalOnly',
																					)}
																				</a>
																			) : null}
																		</div>
																	</div>
																</li>
															))}
														</ul>
													) : (
														<p>{Localizer('ProductCatalogsIsEmpty')}</p>
													)}

													<ValidationMessages meta={meta} id={input.name + '-invalid'} />
												</>
											)}
										</Field>
									</div>

									<div className="roc-catalog-form-wrapper">
										<div className="roc-catalog-form">
											<p className="text-info">{Localizer('ProductCatalogsNote')}</p>
											<p className="roc-catalog-form-info">{Localizer('RequiredFields')}</p>

											<div className="roc-row">
												<div className="roc-col-sm-6">
													<TextBoxField
														name="fullName"
														label={Localizer('FullName')}
														isRequired
													/>
												</div>
												<div className="roc-col-sm-6">
													<TextBoxField name="email" label={Localizer('Email')} isRequired />
												</div>
											</div>

											<div className="roc-row">
												<div className="roc-col-sm-4">
													<TextBoxField
														name="phone"
														label={Localizer('PhoneNumber')}
														isRequired
													/>
												</div>
												<div className="roc-col-sm-4">
													<DropdownField
														label={Localizer('AddressType')}
														name="addressType"
														isRequired
														options={AddressTypeOption}
														addSelectOption={false}
													/>
												</div>
												<div className="roc-col-sm-4">
													<TextBoxField
														name="facilityName"
														label={Localizer('FacilityName')}
														isRequired={false}
													/>
												</div>
											</div>

											<div className="roc-row">
												<div className="roc-col-sm-6">
													<TextBoxField
														name="address1"
														label={Localizer('Address')}
														isRequired
													/>
												</div>
												<div className="roc-col-sm-6">
													<TextBoxField
														name="address2"
														label={Localizer('Address2')}
														isRequired={false}
													/>
												</div>
											</div>

											<div className="roc-row">
												<div className="roc-col-sm-4">
													<TextBoxField name="city" label={Localizer('City')} isRequired />
												</div>
												<div className="roc-col-sm-4">
													{states ? (
														<DropdownField
															label={Localizer('State')}
															name="state"
															isRequired
															options={states}
															addSelectOption
														/>
													) : (
														<TextBoxField
															name="state"
															label={Localizer('State')}
															isRequired
														/>
													)}
												</div>
												<div className="roc-col-sm-4">
													<TextBoxField
														name="postalCode"
														label={Localizer('ZipCode')}
														isRequired
													/>
												</div>
											</div>

											<div className="roc-catalog-form-footer">
												<SimpleCheckbox
													label={Localizer('IsEmailListSignup')}
													checked={isEmailList}
													onChange={(e) => {
														setIsEmailList(e.currentTarget.checked);
													}}
													id="isEmailList"
													ariaLabel={Localizer('IsEmailListSignup')}
													name="isEmailList"
													isRequired={false}
												/>

												<ReCaptchaDisclaimer />

												{submitError ? (
													<InlineAlert message={submitError} error={null} />
												) : null}

												<button
													disabled={submitting}
													type="submit"
													className="roc-btn"
													data-testid="contact-form-submit"
												>
													{Localizer('Submit')} {submitting ? <Spinner light /> : null}
												</button>
											</div>
										</div>
									</div>
								</div>
							</form>
						);
					}}
				/>
			) : (
				<Wysiwyg code={'CatalogRequestFormSubmittedMessage'} />
			)}
		</div>
	);
}

export default ProductCatalogs;
